<script setup lang="ts">
import type { ComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Icon, useToaster } from '@tms-phoenix/ui-vue';
import { QUICK_ACTIONS, executeQuickAction, type QuickActionId } from '@/shared/actions/quick-actions';
import PrintLabelsQuickActionModal from '@/shared/components/actions/PrintLabelsQuickActionModal.vue';
import ReceiveMilkOriginDialog from '@/modules/customer-inventory/components/ReceiveMilkOriginDialog.vue';
import ReceiveMilkDialog from '@/modules/customer-inventory/components/ReceiveMilkDialog.vue';
import ReceiveMultipleMilkDialog from '@/modules/customer-inventory/components/ReceiveMultipleMilkDialog.vue';
import BulkReceiveFailuresDialog from '@/modules/customer-inventory/components/BulkReceiveFailuresDialog.vue';
import AdministerFeedDialog from '@/modules/feed-administration/components/AdministerFeedDialog.vue';
import { useMotherMilkStore, useDonorMilkStore, useEntitlementsStore } from '@/stores';
import { useLocale } from '@/shared/composables/useLocale';
import type {
  ReceiveMilkContainerPayload,
  ReceiveMultipleBottlesPayload,
  ReceiveMotherMilkPayload,
  ReceiveDonorMilkPayload,
  BulkReceiveFailure,
} from '@/modules/customer-inventory/types';

const route = useRoute();
const router = useRouter();
const toast = useToaster();
const { t } = useLocale();
const motherMilkStore = useMotherMilkStore();
const donorMilkStore = useDonorMilkStore();
const entitlementsStore = useEntitlementsStore();

const isMenuOpen = ref(false);
const showPrintLabelsModal = ref(false);
const receiveOriginVisible = ref(false);
const receiveMilkVisible = ref(false);
const receiveDonorMilkVisible = ref(false);
const receiveMultipleVisible = ref(false);
const receiveMultipleOrigin = ref<'mother' | 'donor'>('mother');
const showBulkFailuresDialog = ref(false);
const bulkFailures = ref<BulkReceiveFailure[]>([]);
const administerFeedVisible = ref(false);
const fabContainerRef = ref<HTMLElement | null>(null);

/*
 * Quick Action visibility — reuses the exact same entitlements/plan RBAC
 * evaluation as AppSidebar.vue (same store, same getters, same P1 package
 * gate) so a Quick Action is never shown for a feature the sidebar itself
 * would hide. No independent/duplicated entitlement logic is introduced here.
 */

// Mothers is not part of the P1 package — mirrors AppSidebar.vue's isPlanP1 gate.
const isPlanP1 = computed(() => entitlementsStore.planFlag === 'P1');

// Print Labels — visible if any of the Patient/Mother/Donor label tabs
// (gated identically to AppSidebar's Patients/Mothers/Donors nav entries)
// would be available.
const canPrintPatientLabel = computed(() => entitlementsStore.hasEntitlement('PATIENT_MANAGEMENT'));
const canPrintMotherLabel = computed(() => entitlementsStore.hasEntitlement('MOTHER_MANAGEMENT') && !isPlanP1.value);
const canPrintDonorLabel = computed(() => entitlementsStore.hasEntitlement('DONOR_MANAGEMENT'));
const canPrintLabels = computed(() => canPrintPatientLabel.value || canPrintMotherLabel.value || canPrintDonorLabel.value);

// Receive Mother Milk Container — same entitlement CustomerInventoryView uses
// to show its Mother tab (and its own Receive Milk button), so this stays in
// sync with the Inventory page's Mother tab instead of also requiring
// MOTHER_MANAGEMENT/non-P1.
const canReceiveMotherMilk = computed(() => entitlementsStore.hasEntitlement('inv.mother_milk_receive'));

// Receive Donor Milk Container — same entitlement CustomerInventoryView uses
// to show its Donor tab.
const canReceiveDonorMilk = computed(() => entitlementsStore.hasEntitlement('inv.donor_milk_receive'));

// The single "Receive Milk" quick action is visible if the user can receive
// milk from at least one origin; which origin(s) they're offered inside the
// chooser dialog is narrowed further by canReceiveMotherMilk/canReceiveDonorMilk.
const canReceiveMilk = computed(() => canReceiveMotherMilk.value || canReceiveDonorMilk.value);

// Prepare Feed — identical entitlement set to AppSidebar's Feed Processing nav entry.
const canPrepareFeed = computed(() => entitlementsStore.hasAnyEntitlement([
  'feed.feed_prep_full',
  'feed.feed_prep_donor_only',
  'feed.feed_prep_product',
  'feed.feed_prep_donor_mother',
]));

// Administer Feed — identical entitlement set to AppSidebar's Feed Administration nav entry.
const canAdministerFeed = computed(() => entitlementsStore.hasEntitlement('feed.feed_administer') || entitlementsStore.hasEntitlement('feed.feed_prep_full'));

const QUICK_ACTION_VISIBILITY: Record<QuickActionId, ComputedRef<boolean>> = {
  print: canPrintLabels,
  receive: canReceiveMilk,
  prepare: canPrepareFeed,
  administer: canAdministerFeed,
};

/** QUICK_ACTIONS filtered to only entitled actions, preserving the original order. */
const visibleQuickActions = computed(() => QUICK_ACTIONS.filter((action) => QUICK_ACTION_VISIBILITY[action.id].value));

const shouldShowFab = computed(() => route.name !== 'dashboard' && visibleQuickActions.value.length > 0);

function closeAllMenus() {
  isMenuOpen.value = false;
}

function onDocumentClick(event: MouseEvent) {
  if (!isMenuOpen.value) return;
  if (!fabContainerRef.value) return;

  const target = event.target as Node | null;
  if (target && !fabContainerRef.value.contains(target)) {
    isMenuOpen.value = false;
  }
}

function toggleQuickActions() {
  isMenuOpen.value = !isMenuOpen.value;
}

async function handleMenuAction(action: QuickActionId) {
  isMenuOpen.value = false;
  await executeQuickAction(action, router, {
    openPrintLabels: () => { showPrintLabelsModal.value = true; },
    openReceiveMilk,
    openAdministerFeed: () => { administerFeedVisible.value = true; },
  });
}

/**
 * Opens the "Receive Milk" flow. When the hospital is only entitled to one
 * origin, that origin's dialog is opened directly (no extra click). When
 * both are available, the origin-chooser dialog is shown first.
 */
function openReceiveMilk() {
  if (canReceiveMotherMilk.value && canReceiveDonorMilk.value) {
    receiveOriginVisible.value = true;
    return;
  }
  if (canReceiveMotherMilk.value) {
    receiveMilkVisible.value = true;
    return;
  }
  if (canReceiveDonorMilk.value) {
    receiveDonorMilkVisible.value = true;
  }
}

function onReceiveOriginSelected(origin: 'mother' | 'donor') {
  if (origin === 'mother') {
    receiveMilkVisible.value = true;
  } else {
    receiveDonorMilkVisible.value = true;
  }
}

function handleSwitchToMultipleFromMother() {
  receiveMultipleOrigin.value = 'mother';
  receiveMultipleVisible.value = true;
}

function handleSwitchToMultipleFromDonor() {
  receiveMultipleOrigin.value = 'donor';
  receiveMultipleVisible.value = true;
}

async function onMilkReceived(payload: ReceiveMilkContainerPayload) {
  if (payload.origin !== 'mother') return;
  const ok = await motherMilkStore.addContainer({
    containerId: payload.containerId,
    providerId: payload.providerId,
    volumeMl: payload.volumeMl,
    milkType: payload.milkType,
    status: payload.status,
    location: payload.location,
    storageLocationId: payload.storageLocationId,
    pumpedDate: payload.pumpedDate,
    expiryDate: payload.expiryDate,
  });
  if (ok) {
    toast.success(t('inventory.containerAddedMother', { id: payload.containerId }));
    receiveMilkVisible.value = false;
  } else {
    toast.danger(motherMilkStore.error || t('inventory.failedAddContainer'));
  }
}

async function onDonorMilkReceived(payload: ReceiveMilkContainerPayload) {
  if (payload.origin !== 'donor') return;
  const ok = await donorMilkStore.addContainer({
    containerId: payload.containerId,
    providerId: payload.providerId,
    volumeMl: payload.volumeMl,
    milkType: payload.milkType,
    status: payload.status,
    donorStatus: payload.donorStatus,
    location: payload.location,
    storageLocationId: payload.storageLocationId,
    pumpedDate: payload.pumpedDate,
    expiryDate: payload.expiryDate,
  });
  if (ok) {
    toast.success(t('inventory.containerAddedDonor', { id: payload.containerId }));
    receiveDonorMilkVisible.value = false;
  } else {
    toast.danger(donorMilkStore.error || t('inventory.failedAddDonorContainer'));
  }
}

async function onMultipleMilkReceived(payload: ReceiveMultipleBottlesPayload) {
  if (payload.origin === 'donor') {
    const bottles = payload.bottles.filter((b): b is ReceiveDonorMilkPayload => b.origin === 'donor');
    const { successCount, failed } = await donorMilkStore.addContainersBulk(
      bottles.map((bottle) => ({
        containerId: bottle.containerId,
        providerId: bottle.providerId,
        volumeMl: bottle.volumeMl,
        milkType: bottle.milkType,
        status: bottle.status,
        donorStatus: bottle.donorStatus,
        location: bottle.location,
        storageLocationId: bottle.storageLocationId,
        pumpedDate: bottle.pumpedDate,
        expiryDate: bottle.expiryDate,
      })),
    );
    if (successCount > 0) {
      toast.success(t('inventory.donorBottlesAddedBulk', { count: successCount }));
      receiveMultipleVisible.value = false;
    }
    if (failed.length > 0) {
      bulkFailures.value = failed;
      showBulkFailuresDialog.value = true;
    } else if (successCount === 0) {
      toast.danger(donorMilkStore.error || t('inventory.failedAddDonorBottlesBulk', { count: bottles.length }));
    }
    return;
  }

  if (payload.origin !== 'mother') return;
  const bottles = payload.bottles.filter((b): b is ReceiveMotherMilkPayload => b.origin === 'mother');
  const { successCount, failed } = await motherMilkStore.addContainersBulk(
    bottles.map((bottle) => ({
      containerId: bottle.containerId,
      providerId: bottle.providerId,
      volumeMl: bottle.volumeMl,
      milkType: bottle.milkType,
      status: bottle.status,
      location: bottle.location,
      storageLocationId: bottle.storageLocationId,
      pumpedDate: bottle.pumpedDate,
      expiryDate: bottle.expiryDate,
    })),
  );
  if (successCount > 0) {
    toast.success(t('inventory.motherBottlesAddedBulk', { count: successCount }));
    receiveMultipleVisible.value = false;
  }
  if (failed.length > 0) {
    bulkFailures.value = failed;
    showBulkFailuresDialog.value = true;
  } else if (successCount === 0) {
    toast.danger(motherMilkStore.error || t('inventory.failedAddMotherBottlesBulk', { count: bottles.length }));
  }
}

watch(
  () => route.fullPath,
  () => {
    closeAllMenus();
  },
);

watch(
  () => shouldShowFab.value,
  (show: boolean) => {
    if (!show) {
      isMenuOpen.value = false;
      showPrintLabelsModal.value = false;
      receiveOriginVisible.value = false;
      receiveMilkVisible.value = false;
      receiveDonorMilkVisible.value = false;
      receiveMultipleVisible.value = false;
      administerFeedVisible.value = false;
    }
  },
);

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
});
</script>


<template>
  <div
    v-if="shouldShowFab"
    ref="fabContainerRef"
    class="fixed right-4 bottom-12 z-[70] flex flex-col items-end sm:right-6 lg:right-8"
  >
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-1"
    >
      <div
        v-if="isMenuOpen"
        class="quick-action-menu"
      >
        <button
          v-for="action in visibleQuickActions"
          :key="action.id"
          type="button"
          class="quick-action-item"
          @click="handleMenuAction(action.id)"
        >
          <span
            class="quick-action-label"
            :title="t(action.labelKey)"
          >{{ t(action.labelKey) }}</span>
          <span
            class="quick-action-icon"
            :class="action.iconBgClass"
          >
            <Icon
              :name="action.iconName"
              size-class="h-4 w-4"
              color="text-white"
              :variant="action.iconVariant ?? 'fill'"
            />
          </span>
        </button>
      </div>
    </Transition>

    <button
      type="button"
      class="fab-button"
      :aria-label="t('quickActions.openAriaLabel')"
      aria-haspopup="true"
      :aria-expanded="isMenuOpen"
      @click.stop="toggleQuickActions"
    >
      <span
        class="transition-transform duration-150"
        :class="isMenuOpen ? 'rotate-45' : ''"
      >
        <Icon
          name="Plus"
          size-class="h-5 w-5"
          color="text-white"
        />
      </span>
    </button>
  </div>

  <PrintLabelsQuickActionModal
    :visible="showPrintLabelsModal"
    @update:visible="showPrintLabelsModal = $event"
  />

  <ReceiveMilkOriginDialog
    v-model:visible="receiveOriginVisible"
    :allow-mother="canReceiveMotherMilk"
    :allow-donor="canReceiveDonorMilk"
    @select="onReceiveOriginSelected"
  />

  <ReceiveMilkDialog
    v-model:visible="receiveMilkVisible"
    origin="mother"
    @container-received="onMilkReceived"
    @switch-to-multiple="handleSwitchToMultipleFromMother"
  />

  <ReceiveMilkDialog
    v-model:visible="receiveDonorMilkVisible"
    origin="donor"
    @container-received="onDonorMilkReceived"
    @switch-to-multiple="handleSwitchToMultipleFromDonor"
  />

  <ReceiveMultipleMilkDialog
    v-model:visible="receiveMultipleVisible"
    :origin="receiveMultipleOrigin"
    @containers-received="onMultipleMilkReceived"
  />

  <BulkReceiveFailuresDialog
    v-model:visible="showBulkFailuresDialog"
    :failures="bulkFailures"
  />

  <AdministerFeedDialog
    v-model:visible="administerFeedVisible"
  />
</template>

<style scoped>
.fab-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 3.125rem;
  width: 3.125rem;
  border-radius: 9999px;
  border: none;
  background: radial-gradient(circle at 30% 20%, #3b82f6, #1d4ed8 70%);
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.34);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.fab-button:hover {
  filter: brightness(1.03);
  transform: translateY(-1px);
}

.fab-button:active {
  transform: translateY(0);
}

.quick-action-menu {
  margin-bottom: 0.875rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.625rem;
}

.quick-action-item {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.quick-action-item:hover {
  transform: translateX(-2px);
}

.quick-action-label {
  min-width: 8rem;
  max-width: 12rem;
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid #e2e8f0;
  padding: 0.45rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  color: #334155;
  text-align: left;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.quick-action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.25rem;
  width: 2.25rem;
  border-radius: 9999px;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.22);
}

@media (max-width: 640px) {
  .quick-action-label {
    min-width: 7rem;
    max-width: 9rem;
    font-size: 0.8125rem;
  }

  .quick-action-icon {
    height: 2.125rem;
    width: 2.125rem;
  }
}
</style>
